// pages/Home.jsx and its sections (components/home/*, components/recommendations/HomeDestinations.jsx).
// Every statement here is true of the product today: no rating without
// reviews, no "popular" without a signal, no promise support cannot keep.
import { plural } from "@/lib/format";
import { WOLOF, withWolof } from "./wolof";

const counted = (lang, forms) => (n) => plural(n, forms, { lang });

export const homeL = {
  en: {
    recent: {
      title: "Pick up where you left off",
      clear: "Clear all",
      clearLabel: "Clear recent searches from this device",
      cleared: "Recent searches cleared.",
      undo: "Undo",
      note: "Saved on this device only.",
      kinds: { stays: "Stay", flights: "Flight", cars: "Car rental" },
      guests: counted("en", { one: "# guest", other: "# guests" }),
      travellers: counted("en", { one: "# traveller", other: "# travellers" }),
    },
    featured: {
      note: "Nightly prices without dates. Choose your dates to see the total.",
      seeAll: "See all stays",
      from: { before: "from", after: "/ night" },
      score: (score) => `${score}/5`,
      scoreLabel: (score, count) => `Rated ${score} out of 5 from ${count} reviews`,
      reviews: counted("en", { one: "# review", other: "# reviews" }),
      types: { hotel: "Hotel", apartment: "Apartment", house: "House" },
      stars: counted("en", { one: "# star", other: "# stars" }),
      loading: "Loading stays…",
    },
    destinations: {
      title: "Explore the world",
      moreStays: "Stays on Toukky",
    },
    trust: {
      title: "Booking with Toukky",
      manage: { title: "Your booking, even without an account", body: "Find it with your email and your Toukky booking number.", link: "Manage booking" },
      support: { title: "Help tied to your booking", body: "Write to support with your booking number and follow the reply by email.", link: "Contact support" },
    },
    newsletter: {
      title: "Ideas for your next trip",
      sub: "Travel ideas and Toukky news by email.",
      label: "Email address",
      placeholder: "name@example.com",
      cta: "Subscribe",
      done: "Thanks, you are on the list.",
      failed: "We couldn't save your address. Try again.",
    },
  },
  fr: {
    recent: {
      title: "Reprendre votre recherche",
      clear: "Tout effacer",
      clearLabel: "Effacer les recherches récentes de cet appareil",
      cleared: "Recherches récentes effacées.",
      undo: "Annuler",
      note: "Enregistrées sur cet appareil uniquement.",
      kinds: { stays: "Séjour", flights: "Vol", cars: "Location de voiture" },
      guests: counted("fr", { one: "# voyageur", other: "# voyageurs" }),
      travellers: counted("fr", { one: "# voyageur", other: "# voyageurs" }),
    },
    featured: {
      note: "Prix par nuit, sans dates. Choisissez vos dates pour voir le total.",
      seeAll: "Voir tous les séjours",
      from: { before: "dès", after: "/ nuit" },
      score: (score) => `${score}/5`,
      scoreLabel: (score, count) => `Noté ${score} sur 5 d’après ${count} avis`,
      reviews: counted("fr", { one: "# avis", other: "# avis" }),
      types: { hotel: "Hôtel", apartment: "Appartement", house: "Maison" },
      stars: counted("fr", { one: "# étoile", other: "# étoiles" }),
      loading: "Chargement des séjours…",
    },
    destinations: {
      title: "Explorez le monde",
      moreStays: "Séjours sur Toukky",
    },
    trust: {
      title: "Réserver avec Toukky",
      manage: { title: "Votre réservation, même sans compte", body: "Retrouvez-la avec votre e-mail et votre numéro de réservation Toukky.", link: "Gérer ma réservation" },
      support: { title: "Une aide liée à votre réservation", body: "Écrivez à l’assistance avec votre numéro de réservation, la réponse arrive par e-mail.", link: "Contacter l’assistance" },
    },
    newsletter: {
      title: "Des idées pour votre prochain voyage",
      sub: "Idées de voyage et nouveautés Toukky par e-mail.",
      label: "Adresse e-mail",
      placeholder: "nom@exemple.com",
      cta: "M’inscrire",
      done: "Merci, vous êtes inscrit.",
      failed: "Impossible d’enregistrer votre adresse. Réessayez.",
    },
  },
  es: {
    recent: {
      title: "Retome su búsqueda",
      clear: "Borrar todo",
      clearLabel: "Borrar las búsquedas recientes de este dispositivo",
      cleared: "Búsquedas recientes borradas.",
      undo: "Deshacer",
      note: "Guardadas solo en este dispositivo.",
      kinds: { stays: "Alojamiento", flights: "Vuelo", cars: "Alquiler de coche" },
      guests: counted("es", { one: "# huésped", other: "# huéspedes" }),
      travellers: counted("es", { one: "# viajero", other: "# viajeros" }),
    },
    featured: {
      note: "Precios por noche, sin fechas. Elija sus fechas para ver el total.",
      seeAll: "Ver todos los alojamientos",
      from: { before: "desde", after: "/ noche" },
      score: (score) => `${score}/5`,
      scoreLabel: (score, count) => `Valorado con ${score} sobre 5 en ${count} opiniones`,
      reviews: counted("es", { one: "# opinión", other: "# opiniones" }),
      types: { hotel: "Hotel", apartment: "Apartamento", house: "Casa" },
      stars: counted("es", { one: "# estrella", other: "# estrellas" }),
      loading: "Cargando alojamientos…",
    },
    destinations: {
      title: "Explore el mundo",
      moreStays: "Alojamientos en Toukky",
    },
    trust: {
      title: "Reservar con Toukky",
      manage: { title: "Su reserva, incluso sin cuenta", body: "Encuéntrela con su correo electrónico y su número de reserva Toukky.", link: "Gestionar reserva" },
      support: { title: "Ayuda vinculada a su reserva", body: "Escriba a asistencia con su número de reserva; la respuesta llega por correo electrónico.", link: "Contactar con asistencia" },
    },
    newsletter: {
      title: "Ideas para su próximo viaje",
      sub: "Ideas de viaje y novedades de Toukky por correo electrónico.",
      label: "Correo electrónico",
      placeholder: "nombre@ejemplo.com",
      cta: "Suscribirme",
      done: "Gracias, ya está en la lista.",
      failed: "No pudimos guardar su dirección. Inténtelo de nuevo.",
    },
  },
  pt: {
    recent: {
      title: "Retome a sua pesquisa",
      clear: "Limpar tudo",
      clearLabel: "Limpar as pesquisas recentes deste dispositivo",
      cleared: "Pesquisas recentes limpas.",
      undo: "Anular",
      note: "Guardadas apenas neste dispositivo.",
      kinds: { stays: "Alojamento", flights: "Voo", cars: "Aluguer de carro" },
      guests: counted("pt", { one: "# hóspede", other: "# hóspedes" }),
      travellers: counted("pt", { one: "# viajante", other: "# viajantes" }),
    },
    featured: {
      note: "Preços por noite, sem datas. Escolha as suas datas para ver o total.",
      seeAll: "Ver todos os alojamentos",
      from: { before: "desde", after: "/ noite" },
      score: (score) => `${score}/5`,
      scoreLabel: (score, count) => `Classificado com ${score} em 5 em ${count} avaliações`,
      reviews: counted("pt", { one: "# avaliação", other: "# avaliações" }),
      types: { hotel: "Hotel", apartment: "Apartamento", house: "Casa" },
      stars: counted("pt", { one: "# estrela", other: "# estrelas" }),
      loading: "A carregar alojamentos…",
    },
    destinations: {
      title: "Explore o mundo",
      moreStays: "Alojamentos na Toukky",
    },
    trust: {
      title: "Reservar com a Toukky",
      manage: { title: "A sua reserva, mesmo sem conta", body: "Encontre-a com o seu e-mail e o seu número de reserva Toukky.", link: "Gerir reserva" },
      support: { title: "Ajuda ligada à sua reserva", body: "Escreva ao apoio com o seu número de reserva; a resposta chega por e-mail.", link: "Contactar o apoio" },
    },
    newsletter: {
      title: "Ideias para a sua próxima viagem",
      sub: "Ideias de viagem e novidades da Toukky por e-mail.",
      label: "Endereço de e-mail",
      placeholder: "nome@exemplo.com",
      cta: "Subscrever",
      done: "Obrigado, já está na lista.",
      failed: "Não foi possível guardar o seu endereço. Tente novamente.",
    },
  },
  ar: {
    recent: {
      title: "تابع بحثك",
      clear: "مسح الكل",
      clearLabel: "مسح عمليات البحث الأخيرة من هذا الجهاز",
      cleared: "تم مسح عمليات البحث الأخيرة.",
      undo: "تراجع",
      note: "محفوظة على هذا الجهاز فقط.",
      kinds: { stays: "إقامة", flights: "رحلة جوية", cars: "تأجير سيارة" },
      guests: counted("ar", { zero: "# ضيف", one: "ضيف واحد", two: "ضيفان", few: "# ضيوف", many: "# ضيفًا", other: "# ضيف" }),
      travellers: counted("ar", { zero: "# مسافر", one: "مسافر واحد", two: "مسافران", few: "# مسافرين", many: "# مسافرًا", other: "# مسافر" }),
    },
    featured: {
      note: "أسعار الليلة بدون تواريخ. اختر تواريخك لرؤية المجموع.",
      seeAll: "عرض كل أماكن الإقامة",
      from: { before: "من", after: "/ ليلة" },
      score: (score) => `${score}/5`,
      scoreLabel: (score, count) => `التقييم ${score} من 5 بناءً على ${count} تقييمًا`,
      reviews: counted("ar", { zero: "# تقييم", one: "تقييم واحد", two: "تقييمان", few: "# تقييمات", many: "# تقييمًا", other: "# تقييم" }),
      types: { hotel: "فندق", apartment: "شقة", house: "منزل" },
      stars: counted("ar", { zero: "# نجمة", one: "نجمة واحدة", two: "نجمتان", few: "# نجوم", many: "# نجمة", other: "# نجمة" }),
      loading: "جار تحميل أماكن الإقامة…",
    },
    destinations: {
      title: "استكشف العالم",
      moreStays: "أماكن الإقامة على Toukky",
    },
    trust: {
      title: "الحجز مع Toukky",
      manage: { title: "حجزك حتى بدون حساب", body: "اعثر عليه ببريدك الإلكتروني ورقم حجز Toukky.", link: "إدارة الحجز" },
      support: { title: "مساعدة مرتبطة بحجزك", body: "راسل الدعم مع رقم حجزك، ويصلك الرد بالبريد الإلكتروني.", link: "التواصل مع الدعم" },
    },
    newsletter: {
      title: "أفكار لرحلتك القادمة",
      sub: "أفكار سفر وأخبار Toukky عبر البريد الإلكتروني.",
      label: "البريد الإلكتروني",
      placeholder: "name@example.com",
      cta: "اشترك",
      done: "شكرًا، أنت الآن على القائمة.",
      failed: "تعذر حفظ عنوانك. حاول مرة أخرى.",
    },
  },
  zh: {
    recent: {
      title: "继续您的搜索",
      clear: "全部清除",
      clearLabel: "清除此设备上的最近搜索",
      cleared: "最近搜索已清除。",
      undo: "撤销",
      note: "仅保存在此设备上。",
      kinds: { stays: "住宿", flights: "航班", cars: "租车" },
      guests: counted("zh", { other: "# 位住客" }),
      travellers: counted("zh", { other: "# 位旅客" }),
    },
    featured: {
      note: "未选日期时显示每晚价格。选择日期即可查看总价。",
      seeAll: "查看全部住宿",
      from: { before: "", after: "起 / 晚" },
      score: (score) => `${score}/5`,
      scoreLabel: (score, count) => `评分 ${score}/5，共 ${count} 条点评`,
      reviews: counted("zh", { other: "# 条点评" }),
      types: { hotel: "酒店", apartment: "公寓", house: "独栋房屋" },
      stars: counted("zh", { other: "# 星级" }),
      loading: "正在加载住宿…",
    },
    destinations: {
      title: "探索世界",
      moreStays: "Toukky 上的住宿",
    },
    trust: {
      title: "通过 Toukky 预订",
      manage: { title: "无需账户也能管理预订", body: "凭电子邮箱和 Toukky 预订号即可找到您的预订。", link: "管理预订" },
      support: { title: "与预订关联的帮助", body: "附上预订号联系客服，回复将通过电子邮件发送。", link: "联系客服" },
    },
    newsletter: {
      title: "下一次旅行的灵感",
      sub: "通过电子邮件接收旅行灵感和 Toukky 最新消息。",
      label: "电子邮箱",
      placeholder: "name@example.com",
      cta: "订阅",
      done: "谢谢，您已加入名单。",
      failed: "无法保存您的邮箱地址，请重试。",
    },
  },
};

// Wolof: the presentation texts only, every other word French (i18n/wolof.js).
homeL.wo = withWolof(homeL.fr, WOLOF.home, "home");
